<template>
  <div
    class="flex h-14 w-full items-center justify-between rounded-sm border border-gray-300 px-4"
    :class="{ 'max-w-5xl': maxWidth }"
  >
    <div class="flex items-center space-x-3 text-gray-600">
      <span>
        {{ $t("SCORECARDS.RULE_ENTRY.1") }}
      </span>

      <group-rule-scorecard-menu
        v-if="!readonly"
        v-model="selectedScorecard"
        :scorecards="group.score_cards"
        :readonly="readonly"
      />

      <div v-else>{{ selectedScorecard?.name || "-" }}</div>

      <span>
        {{ $t("SCORECARDS.RULE_ENTRY.2") }}
      </span>

      <status-dropdown
        v-model="scorecardStatus"
        :disabled="readonly || !selectedScorecard"
        :custom-options="scorecardStatusOptions"
        class="group-rule-status-dd"
      />

      <span>
        {{ $t("SCORECARDS.RULE_ENTRY.3") }}
      </span>

      <status-dropdown
        v-model="scorecardGroupStatus"
        :disabled="readonly || !scorecardStatus?.label"
        :custom-options="groupStatusOptions"
        class="group-rule-status-dd"
      />

      <span>
        {{ $t("SCORECARDS.RULE_ENTRY.4") }}
      </span>
    </div>

    <div v-if="!readonly" class="cursor-pointer" @click="discard">
      <icon-base :icon="IconX" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import GroupRuleScorecardMenu from "./GroupRuleScorecardMenu.vue";
import StatusDropdown from "./StatusDropdown.vue";
import IconX from "@/components/icons/deals/IconX.vue";

import type {
  IScorecard,
  IScorecardGroup,
  IScorecardGroupRule,
  IStatusDropdownItem,
} from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "discard"): void;
  (eventName: "update", payload: Partial<IScorecardGroupRule>): void;
}>();

const props = defineProps({
  group: {
    type: Object as PropType<IScorecardGroup>,
    required: true,
  },
  rule: {
    type: Object as PropType<IScorecardGroupRule>,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: Boolean,
    default: true,
  },
});

const selectedScorecard = ref<IScorecard | null>(
  props.group.score_cards.find(
    (scorecard) => scorecard.id === props.rule?.score_card_id
  ) || null
);

const scorecardStatus = ref<IStatusDropdownItem>(scorecardStatusForRule());

const scorecardGroupStatus = ref<IStatusDropdownItem>(
  scorecardGroupStatusForRule()
);

const scorecardStatusOptions = computed(() => {
  const options: IStatusDropdownItem[] =
    selectedScorecard.value?.statuses?.map(({ name, color }) => ({
      label: name,
      color: color,
    })) || [];
  return options;
});

const groupStatusOptions = computed(() => {
  const options: IStatusDropdownItem[] =
    props.group.statuses?.map(({ name, color }) => ({
      label: name,
      color: color,
    })) || [];
  return options;
});

const discard = () => {
  emit("discard");
};

const update = () => {
  const score_card_status_id =
    selectedScorecard.value?.statuses?.find(
      (status) => status.name === scorecardStatus.value?.label
    )?.id || -1;

  const payload = {
    score_card_id: selectedScorecard.value?.id || "",
    score_card_group_status_name: scorecardGroupStatus.value?.label || "",
    score_card_status_id,
  };

  emit("update", payload);
};

function scorecardStatusForRule() {
  const status = selectedScorecard.value?.statuses?.find(
    (status) => status.id === props.rule?.score_card_status_id
  );
  return {
    color: status?.color || "",
    label: status?.name || "",
  };
}

function scorecardGroupStatusForRule() {
  const status = props.group.statuses?.find(
    (status) => status.name === props.rule?.score_card_group_status_name
  );
  return {
    color: status?.color || "",
    label: status?.name || "",
  };
}

watch(selectedScorecard, () => {
  const statusDefaultVal = {
    color: "",
    label: "",
  };
  scorecardStatus.value = { ...statusDefaultVal };
  scorecardGroupStatus.value = { ...statusDefaultVal };
});

watch([scorecardStatus, scorecardGroupStatus], update);
</script>
